import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import copy from 'copy-to-clipboard';
import classNames from 'classnames';

import BaseDialog from 'core/components/base-dialog';
import { Label } from 'core/components/label';
import { Button } from 'core/components/button';
import { Loading } from 'core/components/loading';
import { useToast } from 'core/components/toast';
import useSession from 'core/hooks/use-session';
import { useStore } from 'core/store';
import { OfferStatus } from 'core/store/offer-store';
import { track } from 'core/helpers/analytics';
import emailIcon from 'core/assets/svg/email-white.svg';
import downloadIcon from 'core/assets/svg/download-doc-white.svg';
import useStyles from './farm-offer-sharing-options.styles';

const FarmOfferSharingOptions = observer(({ open, onClose }) => {
  const classes = useStyles();
  const toast = useToast();
  const { offer: offerStore } = useStore();
  const { offer, downloadQuote, status } = offerStore;
  const { id: offerId, quote } = offer;
  const { isAgency } = useSession();

  const rep = quote?.rep;
  const hasRep = Boolean(rep?.length);

  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      track('Farm offer sharing options - viewed', { offerId, isAgency });
    }
  }, [open, offerId, isAgency]);

  const handleSendEmail = async () => {
    track('Farm offer sharing options - Email offer clicked', { offerId, isAgency });
    setSending(true);
    try {
      const result = await offerStore.sendOfferLinkToRep(offerId);
      if (result?.message) {
        toast.notify({
          type: 'info',
          message: "Couldn't email the rep — use Copy offer link instead."
        });
      } else {
        toast.notify({
          type: 'success',
          message: `Offer link emailed to ${rep}.`
        });
        onClose();
      }
    } catch {
      toast.notify({
        type: 'error',
        message: "Couldn't email the offer."
      });
    } finally {
      setSending(false);
    }
  };

  const handleCopyOfferLink = () => {
    track('Farm offer sharing options - Offer link copied', { offerId, isAgency });
    const { protocol, host } = window.location;
    const offerLink = `${protocol}//${host}/offer/${offerId}`;
    const copied = copy(offerLink);
    if (copied) {
      toast.notify({
        type: 'success',
        message: 'The link to the offer has been copied to your clipboard.'
      });
    } else {
      toast.notify({
        type: 'error',
        message: `We could not copy the link, but here it is: ${offerLink}`
      });
    }
  };

  const handleDownloadOffer = useCallback(() => {
    track('Farm offer sharing options - Offer PDF downloaded', {
      offerId,
      isAgency,
      entryPoint: 'Farm offer sharing options modal'
    });
    // Farm-based offers (F/Z/Y) generate the Offer PDF via the Zywave DocGen pipeline,
    // not the DocRaptor/docamatic /review capture (which 500s for farm offers). This modal
    // only renders for farm offers, so always route through downloadQuote with the 'quote'
    // doc context — the same path the sidebar Documents & sharing card uses for farm.
    downloadQuote({ bypassCache: false, docContext: 'quote' });
  }, [downloadQuote, offerId, isAgency]);

  const handleDownloadApplicationPdf = useCallback(() => {
    track('Farm offer sharing options - Application PDF downloaded', {
      offerId,
      isAgency,
      entryPoint: 'Farm offer sharing options modal'
    });
    downloadQuote({ bypassCache: false });
  }, [downloadQuote, offerId, isAgency]);

  return (
    <BaseDialog open={open} onClose={onClose} className={classes.modal} size="md">
      <div className={classes.container}>
        <Label type="greenBig" className={classes.title}>
          Offer sharing options
        </Label>

        <div className={classes.optionCards}>
          {hasRep && (
            <div className={classes.optionCard}>
              <div className={classes.optionCardMain}>
                <div className={classes.optionText}>
                  <Label className={classes.optionTitle}>Send offer to acting agent</Label>
                  <Label className={classes.optionDescription} variant="body2">
                    Sends {rep} a link to the current offer. The link only applies to this version.
                  </Label>
                </div>
                <Button
                  className={classNames(classes.actionButton, classes.emailButton)}
                  onClick={handleSendEmail}
                  disabled={sending}
                >
                  {sending ? (
                    <Loading type="secondary" noBackground size={20} />
                  ) : (
                    <>
                      <img src={emailIcon} alt="" className={classNames(classes.buttonIcon, classes.emailIcon)} /> Email
                      offer
                    </>
                  )}
                </Button>
              </div>
              <div className={classes.cardFooter}>
                <Label className={classes.cardFooterText} inlineDisplay>
                  Want to send it yourself?{' '}
                  <span
                    className={classes.footerLink}
                    onClick={handleCopyOfferLink}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleCopyOfferLink();
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    Copy offer link
                  </span>
                </Label>
              </div>
            </div>
          )}

          <div className={classes.optionCard}>
            <div className={classes.optionCardMain}>
              <div className={classes.optionText}>
                <Label className={classes.optionTitle}>Share a PDF of the offer</Label>
                <Label className={classes.optionDescription}>Downloads a view-only PDF you can forward or print.</Label>
              </div>
              <Button
                className={classNames(classes.actionButton, classes.downloadButton)}
                onClick={handleDownloadOffer}
              >
                {status === OfferStatus.DownloadingOffer ? (
                  <>
                    <Loading type="secondary" noBackground size={20} /> Generating PDF...
                  </>
                ) : (
                  <>
                    <img src={downloadIcon} alt="" className={classes.buttonIcon} /> Download offer PDF
                  </>
                )}
              </Button>
            </div>
            <div className={classes.cardFooter}>
              <Label className={classes.cardFooterText} inlineDisplay>
                Need the full application and required forms?{' '}
                {status === OfferStatus.DownloadingQuote ? (
                  <>
                    <Loading type="secondary" noBackground size={14} />
                    <span className={classes.generatingPdfText}>Generating PDF...</span>
                  </>
                ) : (
                  <span
                    className={classes.footerLink}
                    onClick={handleDownloadApplicationPdf}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleDownloadApplicationPdf();
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    Download Application PDF
                  </span>
                )}
              </Label>
            </div>
          </div>
        </div>
      </div>
    </BaseDialog>
  );
});

export default FarmOfferSharingOptions;
